import { Injectable } from '@angular/core';
import { BehaviorSubject, map, Observable } from 'rxjs';

export const USER_ROLE = {
  AdministratorSuper: 'SV.DatabaseOnline:AdministratorSuper',
  Administrator: 'SV.DatabaseOnline:Administrator',
  TerminAdministrator: 'SV.TerminOnline:AdministratorTermin',
} as const;

type ObjectValues<T> = T[keyof T];
export type UserRole = ObjectValues<typeof USER_ROLE>;

const ROLE_PARENT_ROLES: Partial<Record<UserRole, UserRole[]>> = {
  [USER_ROLE.AdministratorSuper]: [USER_ROLE.Administrator, USER_ROLE.TerminAdministrator],
};

@Injectable({
  providedIn: 'root',
})
export class RoleService {
  private userRolesSubject = new BehaviorSubject<string[]>([]);
  private userRoles$ = this.userRolesSubject.asObservable();

  setRoles(roles: UserRole[]): void {
    this.userRolesSubject.next(roles);
    console.log('RoleService userRoles :>> ', roles);
  }

  private getEffectiveRoles(roles: string[]): string[] {
    const effectiveRoles = new Set<string>();

    const addRoleWithParents = (role: string): void => {
      if (effectiveRoles.has(role)) {
        return;
      }

      effectiveRoles.add(role);

      const parentRoles = ROLE_PARENT_ROLES[role as UserRole] ?? [];

      parentRoles.forEach((parentRole) => {
        addRoleWithParents(parentRole);
      });
    };

    roles.forEach((role) => {
      addRoleWithParents(role);
    });

    return Array.from(effectiveRoles);
  }

  hasRole(role: string): Observable<boolean> {
    return this.userRoles$.pipe(map((roles) => this.getEffectiveRoles(roles).includes(role)));
  }

  hasAnyRole(roles: string[]): Observable<boolean> {
    return this.userRoles$.pipe(
      map((userRoles) => {
        const effectiveRoles = this.getEffectiveRoles(userRoles);

        return roles.some((role) => effectiveRoles.includes(role));
      }),
    );
  }

  getRoles(): Observable<string[]> {
    return this.userRoles$;
  }
}
